/**
 * Family Photos Batch Drop-Zone
 * ------------------------------
 * Route: /family/:treeId/photos-batch
 *
 * Editors/admins can drag many photos in at once and assign each one
 * to a specific member of the tree. Photos are uploaded to the
 * storage layer (not stored inline as base64) so a 300+ ancestor
 * tree stays lean.
 *
 * Left rail  — searchable list of members WITHOUT a photo.
 * Right rail — drop-zone + queue. Each dropped file becomes a card
 *              with a member combobox (fuzzy search by name) and an
 *              Upload button. Successful uploads remove the row from
 *              the left rail and mark the card done.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Search, ImageIcon, CheckCircle2, X, Loader2,
  AlertCircle, UserCircle2,
} from "lucide-react";

import { http, resolveImage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const MAX_BYTES = 6 * 1024 * 1024; // must match backend
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/gif"];

/**
 * Turn a File into a base64 data URL. Fails gracefully on non-image
 * mime types or oversized files so a bad drop never spoils the batch.
 */
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!ALLOWED.includes(file.type)) {
      reject(new Error(`Unsupported image type: ${file.type || "unknown"}`));
      return;
    }
    if (file.size > MAX_BYTES) {
      reject(new Error(`${file.name} is larger than 6 MB.`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

function fullName(m) {
  return [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ").trim();
}

/**
 * Very small fuzzy match: score = length of longest common prefix +
 * 0.5 * substring hits. Fast enough for 500 members in the client.
 */
function matchScore(query, member) {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const name = fullName(member).toLowerCase();
  if (!name) return 0;
  if (name === q) return 1000;
  if (name.startsWith(q)) return 500 + q.length;
  if (name.includes(q)) return 100 + q.length;
  // Split-word match — helps when user types just the surname.
  const parts = name.split(/\s+/);
  if (parts.some((p) => p.startsWith(q))) return 60 + q.length;
  return 0;
}

// ----------------------------------------------------------------
// Member combobox — lightweight; avoids a heavy shadcn dependency
// so this page stays snappy even with 300+ members loaded.
// ----------------------------------------------------------------
function MemberPicker({ members, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = useMemo(
    () => (value ? members.find((m) => m.id === value) : null),
    [members, value],
  );

  const results = useMemo(() => {
    if (!q.trim()) return members.slice(0, 30);
    return members
      .map((m) => [m, matchScore(q, m)])
      .filter(([, s]) => s > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([m]) => m);
  }, [members, q]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        data-testid="member-picker-toggle"
        className="w-full text-left px-3 py-2 rounded-lg border border-neutral-300 bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {selected ? (
          <span className="flex items-center gap-2">
            <UserCircle2 className="w-4 h-4 opacity-60" />
            <span className="font-medium truncate">{fullName(selected)}</span>
            {selected.birth_date ? (
              <span className="text-xs opacity-60">· b. {String(selected.birth_date).slice(0, 4)}</span>
            ) : null}
          </span>
        ) : (
          <span className="text-neutral-500">Choose a family member…</span>
        )}
      </button>
      {open && !disabled ? (
        <div className="absolute z-20 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-neutral-100">
            <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-neutral-50">
              <Search className="w-4 h-4 opacity-50" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Type a name…"
                className="w-full bg-transparent outline-none text-sm"
                data-testid="member-picker-search"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {results.length === 0 ? (
              <div className="px-3 py-4 text-sm text-neutral-500">No matches — try a different name.</div>
            ) : (
              results.map((m) => (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => {
                    onChange(m.id);
                    setOpen(false);
                    setQ("");
                  }}
                  data-testid={`member-picker-option-${m.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center justify-between"
                >
                  <span className="truncate">
                    <span className="font-medium">{fullName(m)}</span>
                    {m.birth_place ? (
                      <span className="text-xs opacity-60 ml-2">{m.birth_place}</span>
                    ) : null}
                  </span>
                  {m.death_date ? (
                    <span className="text-xs opacity-60 ml-2">
                      d. {String(m.death_date).slice(0, 4)}
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------
// Main page
// ----------------------------------------------------------------
export default function FamilyPhotosBatch() {
  const { treeId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [tree, setTree] = useState(null);
  const [members, setMembers] = useState([]);      // members without a photo
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState([]);          // [{id, file, dataUrl, previewUrl, memberId, status, error}]
  const [leftQuery, setLeftQuery] = useState("");
  const dragCounter = useRef(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=/family/${treeId}/photos-batch`);
    }
  }, [user, authLoading, navigate, treeId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, m] = await Promise.all([
        http.get(`/family-trees/${treeId}`),
        http.get(`/family-trees/${treeId}/members/without-photo`),
      ]);
      setTree(t.data);
      setMembers(m.data.items || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not load.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [treeId]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredLeft = useMemo(() => {
    const q = leftQuery.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => fullName(m).toLowerCase().includes(q));
  }, [members, leftQuery]);

  const addFiles = useCallback(
    async (files) => {
      const list = Array.from(files || []);
      if (list.length === 0) return;
      const added = [];
      for (const file of list) {
        try {
          const dataUrl = await fileToDataUrl(file);
          added.push({
            id: crypto.randomUUID(),
            file,
            dataUrl,
            previewUrl: URL.createObjectURL(file),
            memberId: null,
            status: "ready",
            error: null,
          });
        } catch (err) {
          toast.error(err.message || "Could not add file.");
        }
      }
      if (added.length) {
        setQueue((prev) => [...added, ...prev]);
        toast.success(`Added ${added.length} photo${added.length > 1 ? "s" : ""} to the queue.`);
      }
    },
    [],
  );

  // ---------- Drag & drop handlers ----------
  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setDragging(true);
  }, []);
  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setDragging(false);
  }, []);
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setDragging(false);
      addFiles(e.dataTransfer?.files);
    },
    [addFiles],
  );

  const upload = useCallback(
    async (row) => {
      if (!row.memberId) {
        toast.error("Pick a family member first.");
        return;
      }
      setQueue((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: "uploading", error: null } : r)));
      try {
        const res = await http.post(
          `/family-trees/${treeId}/members/${row.memberId}/photo`,
          { data_url: row.dataUrl },
        );
        setQueue((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: "done", uploadedUrl: res.data.photo } : r)));
        // Remove that member from the left rail so they don't get a second photo by accident.
        setMembers((prev) => prev.filter((m) => m.id !== row.memberId));
        toast.success("Photo attached.");
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || "Upload failed.";
        setQueue((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: "error", error: msg } : r)));
        toast.error(msg);
      }
    },
    [treeId],
  );

  const uploadAll = useCallback(async () => {
    const pending = queue.filter((r) => r.status === "ready" && r.memberId);
    if (pending.length === 0) {
      toast.info("Assign each photo to a family member first.");
      return;
    }
    for (const r of pending) {
      // Sequential — keeps the storage layer + Mongo writes gentle.
      // eslint-disable-next-line no-await-in-loop
      await upload(r);
    }
  }, [queue, upload]);

  const removeFromQueue = useCallback((id) => {
    setQueue((prev) => {
      const row = prev.find((r) => r.id === id);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return prev.filter((r) => r.id !== id);
    });
  }, []);

  const assign = useCallback((rowId, memberId) => {
    setQueue((prev) => prev.map((r) => (r.id === rowId ? { ...r, memberId } : r)));
  }, []);

  // Sanity: revoke object URLs on unmount.
  useEffect(
    () => () => {
      queue.forEach((r) => r.previewUrl && URL.revokeObjectURL(r.previewUrl));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const doneCount = queue.filter((r) => r.status === "done").length;
  const readyCount = queue.filter((r) => r.status === "ready").length;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="family-photos-batch">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link
              to={`/family/${treeId}`}
              className="inline-flex items-center gap-1 text-sm text-indigo-700 hover:underline"
              data-testid="back-to-tree"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {tree?.name || "family tree"}
            </Link>
            <h1 className="mt-2 text-3xl sm:text-4xl font-display font-semibold text-neutral-900">
              Photos for {tree?.name || "the family"}
            </h1>
            <p className="mt-1 text-sm text-neutral-600 max-w-2xl">
              Drop many photos below, match each one to a family member, and press{" "}
              <span className="font-semibold">Upload all</span>. The queue processes them one at a time so
              nothing gets lost.
            </p>
          </div>
          {tree?.slug ? (
            <div className="hidden sm:block text-right">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Remaining</div>
              <div className="text-3xl font-display font-semibold text-indigo-700" data-testid="remaining-count">
                {members.length}
              </div>
              <div className="text-xs text-neutral-500">members without a photo</div>
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="mb-6 flex items-center gap-2 p-4 rounded-lg border border-rose-200 bg-rose-50 text-rose-700">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="grid lg:grid-cols-[320px_1fr] gap-6">
            {/* Left rail — members without photos */}
            <aside className="bg-white rounded-xl border border-neutral-200 p-4 h-fit lg:sticky lg:top-6">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 opacity-50" />
                <input
                  value={leftQuery}
                  onChange={(e) => setLeftQuery(e.target.value)}
                  placeholder="Search members…"
                  className="w-full bg-transparent outline-none text-sm"
                  data-testid="left-rail-search"
                />
              </div>
              <div className="max-h-[520px] overflow-y-auto -mx-2">
                {filteredLeft.length === 0 ? (
                  <div className="px-2 py-8 text-center text-sm text-neutral-500">
                    {members.length === 0
                      ? "Every family member has a photo. 🎉"
                      : "No matches for that search."}
                  </div>
                ) : (
                  filteredLeft.map((m) => (
                    <div
                      key={m.id}
                      className="px-2 py-1.5 rounded hover:bg-indigo-50 flex items-center justify-between"
                      data-testid={`unmatched-member-${m.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{fullName(m) || "Unnamed"}</div>
                        <div className="text-xs text-neutral-500 truncate">
                          {[m.birth_date && `b. ${String(m.birth_date).slice(0, 4)}`, m.death_date && `d. ${String(m.death_date).slice(0, 4)}`, m.birth_place]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </aside>

            {/* Right rail — drop-zone + queue */}
            <section>
              {/* Drop-zone */}
              <div
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onDrop}
                data-testid="dropzone"
                className={`relative rounded-2xl border-2 border-dashed transition-colors ${
                  dragging
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-neutral-300 bg-white hover:border-indigo-300"
                }`}
              >
                <div className="p-10 text-center">
                  <Upload
                    className={`w-10 h-10 mx-auto mb-3 ${dragging ? "text-indigo-600" : "text-neutral-400"}`}
                  />
                  <div className="font-medium text-neutral-800">
                    Drag photos here, or{" "}
                    <label className="text-indigo-700 hover:underline cursor-pointer">
                      pick from your device
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        data-testid="file-input"
                        onChange={(e) => {
                          addFiles(e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                  <div className="mt-1 text-xs text-neutral-500">
                    JPEG, PNG, WebP, HEIC or GIF · up to 6 MB each · you can drop many at once.
                  </div>
                </div>
              </div>

              {/* Queue toolbar */}
              {queue.length > 0 ? (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-neutral-600" data-testid="queue-summary">
                    {queue.length} photo{queue.length > 1 ? "s" : ""} in queue
                    {doneCount > 0 ? ` · ${doneCount} uploaded` : ""}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        queue.forEach((r) => r.previewUrl && URL.revokeObjectURL(r.previewUrl));
                        setQueue([]);
                      }}
                      className="px-3 py-1.5 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-100"
                      data-testid="clear-queue"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={uploadAll}
                      disabled={readyCount === 0}
                      className="px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
                      data-testid="upload-all"
                    >
                      Upload all ({readyCount})
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Queue */}
              <div className="mt-4 space-y-3">
                {queue.map((row) => (
                  <QueueCard
                    key={row.id}
                    row={row}
                    members={members}
                    onAssign={(mid) => assign(row.id, mid)}
                    onUpload={() => upload(row)}
                    onRemove={() => removeFromQueue(row.id)}
                  />
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function QueueCard({ row, members, onAssign, onUpload, onRemove }) {
  const done = row.status === "done";
  const uploading = row.status === "uploading";
  const errored = row.status === "error";

  return (
    <div
      className={`p-4 rounded-xl border flex gap-4 items-start bg-white ${
        done ? "border-emerald-300 bg-emerald-50" : errored ? "border-rose-300 bg-rose-50" : "border-neutral-200"
      }`}
      data-testid={`queue-card-${row.id}`}
    >
      <div className="shrink-0 relative">
        <img
          src={done && row.uploadedUrl ? resolveImage(row.uploadedUrl) : row.previewUrl}
          alt=""
          className="w-24 h-24 object-cover rounded-lg border border-neutral-200"
        />
        {done ? (
          <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow">
            <CheckCircle2 className="w-4 h-4" />
          </div>
        ) : null}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <ImageIcon className="w-4 h-4 opacity-50" />
          <span className="text-sm font-medium truncate">{row.file.name}</span>
          <span className="text-xs text-neutral-500">
            {(row.file.size / 1024).toFixed(0)} KB
          </span>
        </div>
        <MemberPicker members={members} value={row.memberId} onChange={onAssign} disabled={done || uploading} />
        {errored ? (
          <div className="mt-2 text-xs text-rose-700 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" />
            {row.error}
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        {!done ? (
          <button
            type="button"
            onClick={onUpload}
            disabled={!row.memberId || uploading}
            className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-neutral-300 disabled:cursor-not-allowed inline-flex items-center gap-1"
            data-testid={`queue-upload-${row.id}`}
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? "Uploading…" : "Upload"}
          </button>
        ) : (
          <span className="px-3 py-1.5 text-xs rounded-lg bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Done
          </span>
        )}
        <button
          type="button"
          onClick={onRemove}
          disabled={uploading}
          className="px-3 py-1.5 text-xs rounded-lg border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid={`queue-remove-${row.id}`}
        >
          <X className="w-3 h-3" />
          {done ? "Hide" : "Remove"}
        </button>
      </div>
    </div>
  );
}
